import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { faArrowDown, faArrowUp, faEdit, faEllipsisV, faPlus, faTrash } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Dropdown, Flex, Space, Typography } from "antd"
import { CSSProperties, useContext, useState } from "react"
import { Workarea } from "../../../../domain/job"
import { WorkareaGroup } from "../../../../domain/workareas/group"
import { sum } from "../../../../helpers/array"
import { hasValue } from "../../../../helpers/nullable"
import { pluralize } from "../../../../helpers/strings"
import VSpace from "../../../common/VSpace"
import { centsToDollarsDisplay } from "../../../displayFormatters"
import { ItemsContext } from "../../../Items/ItemsRoot"
import { SortableItem, tagWorkareaGroup } from "./dndModel"
import Grabber from "./Grabber"
import PricingTypeDisplay from "../../PricingTypeDisplay"
import Sortable from "./Sortable"
import { SortableWorkareaComponent, WorkareaComponent } from "./WorkareaComponent"
import DeleteWorkareaGroupModal from "./DeleteWorkareaGroupModal"
import { getChildrenWorkareasFromGroupedWorkareas, GroupedWorkareas } from "./workareaGroupsListModel"
import EmptyGroupIndicator from "./EmptyGroupIndicator"
import HighlightIndicator from "./HighlightIndicator"
import ApprovedOrOnHoldDisplay from "./ApprovedOrOnHoldDisplay"
import PriceSummary from "./PriceSummary"
import JobPlanContext from "../JobPlanContext"

const { Text } = Typography

export const groupWrapperStyle: CSSProperties = {
  marginBottom: 16
}
export const groupCardStyle: CSSProperties = {
  border: '1px solid #e9e9e9',
  backgroundColor: '#f5f5f5',
  borderRadius: 4,
  padding: 16,
  color: '#1e1e1e'
}

export const onHoldStyle: CSSProperties = {
  backgroundColor: '#fff1f0'
}


type WorkareaGroupComponentProps = {
  workareaGroup: WorkareaGroup
  workareas: Workarea[]
  groupedWorkareas?: GroupedWorkareas
  onEdit: () => void
  activeItem?: SortableItem
  overItem?: SortableItem
  onAddWorkarea?: () => void
  onMoveUp: () => void
  onMoveDown: () => void
  onMoveWorkareaUp: (workareaId: number) => void
  onMoveWorkareaDown: (workareaId: number) => void
  onMoveWorkareaTo: (workareaId: number) => void
  onRemoveWorkareaFromGroup: (workareaId: number) => void
}

type WorkareaGroupCardProps = Pick<WorkareaGroupComponentProps, 'workareaGroup' | 'workareas' | 'onEdit' | 'onAddWorkarea' | 'onMoveUp' | 'onMoveDown'>

const WorkareaGroupCard = ({ workareaGroup, workareas, onEdit, onAddWorkarea, onMoveUp, onMoveDown }: WorkareaGroupCardProps) => {
  const { workareaSummaries, jobData: { jobId } } = useContext(ItemsContext)
  const { selectGroup } = useContext(JobPlanContext)
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false)

  const totalPrice = sum(workareas, w => {
    const summary = workareaSummaries.find(s => s.workareaId === w.id)
    return summary?.finalPrice || 0
  })

  const estimatedHours = sum(workareas, w => {
    const summary = workareaSummaries.find(s => s.workareaId === w.id)
    return summary?.estimatedHours || 0
  })

  const estimatedPrice = sum(workareas, w => {
    const summary = workareaSummaries.find(s => s.workareaId === w.id)
    return summary?.estimatedPrice || 0
  })

  return <div style={{ ...groupCardStyle, ...(workareaGroup.isOnHold ? onHoldStyle : {}) }}>
    <Flex>
      <Grabber />
      <Flex justify="space-between" align="center" onClick={() => selectGroup(workareaGroup.id)} style={{ cursor: 'pointer', width: '100%' }}>
        <Space size="large">
          <VSpace>
            <strong style={{ fontWeight: 600 }}>{workareaGroup.name}</strong>
            <Space>
              <Text type="secondary">
                <PricingTypeDisplay pricingType={workareaGroup.pricingType} />
              </Text>
              <ApprovedOrOnHoldDisplay workareaGroup={workareaGroup} />
            </Space>
          </VSpace>
        </Space>
        <Space>
          <Flex gap={20}>
            <PriceSummary
              finalPrice={totalPrice}
              hideFinalPrice
              estimatedPrice={estimatedPrice}
              estimatedHours={estimatedHours}
              pricingType={workareaGroup.pricingType}
            />
            <Space direction="vertical">
              <strong>{centsToDollarsDisplay(totalPrice)}</strong>
              <Text type="secondary">{workareas.length} {pluralize(workareas.length, 'Workarea')}</Text>
            </Space>
          </Flex>
          <Dropdown
            trigger={['click']}
            menu={{
              items: [
                {
                  key: 'add',
                  label: 'Add Workarea',
                  icon: <FontAwesomeIcon icon={faPlus} />,
                  onClick: (e) => {
                    e.domEvent.stopPropagation()
                    onAddWorkarea?.()
                  },
                  disabled: workareaGroup.isApproved
                },
                {
                  key: 'edit',
                  label: 'Edit Group',
                  icon: <FontAwesomeIcon icon={faEdit} />,
                  onClick: (e) => {
                    e.domEvent.stopPropagation()
                    onEdit()
                  }
                },
                {
                  key: 'delete',
                  label: 'Delete Group',
                  icon: <FontAwesomeIcon icon={faTrash} />,
                  onClick: (e) => {
                    e.domEvent.stopPropagation()
                    setDeleteModalOpen(true)
                  }
                },
                {
                  key: 'move-up',
                  label: <Space><FontAwesomeIcon icon={faArrowUp} />Move Up</Space>,
                  onClick: (e) => {
                    e.domEvent.stopPropagation()
                    onMoveUp()
                  }
                },
                {
                  key: 'move-down',
                  label: <Space><FontAwesomeIcon icon={faArrowDown} />Move Down</Space>,
                  onClick: (e) => {
                    e.domEvent.stopPropagation()
                    onMoveDown()
                  }
                }
              ]
            }}
          >
            <Button 
              type="text" 
              icon={<FontAwesomeIcon icon={faEllipsisV} />} 
              onClick={e => e.stopPropagation()}
            />
          </Dropdown>
        </Space>
      </Flex>
    </Flex>

    <DeleteWorkareaGroupModal
      isOpen={isDeleteModalOpen}
      jobId={jobId}
      onCancel={() => setDeleteModalOpen(false)}
      workareaGroupId={workareaGroup.id}
      workareaGroupName={workareaGroup.name}
      workareaCount={workareas.length}
    />
  </div>
}

export const WorkareaGroupComponent = ({
  workareaGroup,
  workareas = [],
  onEdit,
  groupedWorkareas,
  activeItem,
  overItem,
  onAddWorkarea,
  onMoveUp,
  onMoveDown,
  onMoveWorkareaUp,
  onMoveWorkareaDown,
  onMoveWorkareaTo,
  onRemoveWorkareaFromGroup,
}: WorkareaGroupComponentProps) => {
  const [visibleChildren, setVisibleChildren] = useState<Record<number, boolean>>({});
  const isCollapsed = workareaGroup.isHidden && workareas.length === 1

  if (isCollapsed) {
    return <div style={groupWrapperStyle}>
      <WorkareaComponent
        workarea={workareas[0]}
        workareaGroup={workareaGroup}
        onMoveUp={onMoveUp}
        onMoveDown={onMoveDown}
        onMoveTo={() => onMoveWorkareaTo(workareas[0].id)}
        onRemoveFromGroup={() => onRemoveWorkareaFromGroup(workareas[0].id)}
      />
    </div>
  }

  const toggleVisibility = (workareaId: number) => {
    setVisibleChildren(prevState => ({
      ...prevState,
      [workareaId]: !prevState[workareaId]
    }));
  };

  const shouldShowHighlight = (workarea: Workarea) => {
    if (overItem?.id !== workarea.id || !hasValue(activeItem)) {
      return false
    }

    if (activeItem.type === 'group' && activeItem.isHidden) {
      return true
    }

    if (activeItem.type === 'workarea' && activeItem.workareaGroupId !== workareaGroup.id) {
      return true
    }

    return false
  }

  return <div style={groupWrapperStyle}>
    <WorkareaGroupCard
      workareaGroup={workareaGroup}
      workareas={workareas}
      onEdit={onEdit}
      onAddWorkarea={onAddWorkarea}
      onMoveUp={onMoveUp}
      onMoveDown={onMoveDown}
    />
    {
      workareas.length === 0 && (
        <EmptyGroupIndicator groupId={workareaGroup.id} onAddNewWorkarea={onAddWorkarea} />
      )
    }
    <SortableContext items={workareas} strategy={verticalListSortingStrategy}>
      {workareas.map((w, index) => {
        const isChildWorkarea = hasValue(w.masterWorkareaId);

        const isLast = !workareas.some((x, i) => i > index && !hasValue(x.masterWorkareaId));

        if (isChildWorkarea) return

        const childWorkareas = getChildrenWorkareasFromGroupedWorkareas(workareaGroup.id, w.id, groupedWorkareas)
        const childWorkareasCount = childWorkareas.length;
        const isVisible = visibleChildren[w.id];

        return (
          <div key={w.id} style={{ position: 'relative' }}>
            <SortableWorkareaComponent
              workarea={w}
              workareaGroup={workareaGroup}
              groupedWorkareas={groupedWorkareas}
              isLast={isLast && (childWorkareasCount === 0 || !isVisible)}
              onMoveUp={() => onMoveWorkareaUp(w.id)}
              onMoveDown={() => onMoveWorkareaDown(w.id)}
              onMoveTo={() => onMoveWorkareaTo(w.id)}
              onRemoveFromGroup={() => onRemoveWorkareaFromGroup(w.id)}
            />

            {childWorkareasCount > 0 && (
              <>
                {isVisible && (
                  childWorkareas.map((childWorkarea, childIndex) => (
                    <SortableWorkareaComponent
                      key={childWorkarea.id}
                      workarea={childWorkarea}
                      workareaGroup={workareaGroup}
                      groupedWorkareas={groupedWorkareas}
                      isLast={isLast && childIndex === childWorkareasCount - 1}
                      onMoveUp={() => onMoveWorkareaUp(w.id)}
                      onMoveDown={() => onMoveWorkareaDown(w.id)}
                      onMoveTo={() => onMoveWorkareaTo(w.id)}
                      onRemoveFromGroup={() => onRemoveWorkareaFromGroup(w.id)}
                    />
                  ))
                )}

                <div style={{ borderLeft: isLast ? undefined : '2px solid #e9e9e9', marginLeft: '24px', paddingLeft: '20px' }}>
                  <Button
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleVisibility(w.id)}
                    type="link"
                  >
                    {isVisible ? `Hide ${childWorkareasCount}` : `View ${childWorkareasCount} more`}
                  </Button>
                </div>
              </>
            )}

            {
              shouldShowHighlight(w)
              && <HighlightIndicator isChildWorkarea />
            }
          </div>
        );
      })}
    </SortableContext>
  </div >
}

export const SortableWorkareaGroupComponent = (props: WorkareaGroupComponentProps) => {
  return <Sortable
    id={props.workareaGroup.id}
    data={tagWorkareaGroup(props.workareaGroup)}
    activeStyle={{ visibility: 'hidden' }}
  >
    <WorkareaGroupComponent {...props} />
  </Sortable>
}
